import { makeAutoObservable } from "mobx";
import { RArray } from "../collections";
import { ProductInstanceCreatorState, } from "../ordering";
import { PCustomParameterInstanceCreator } from "./PCustomParameterInstanceCreator";
import { PModifierInstanceCreator } from "./PModifierInstanceCreator";
import { PProductError } from "./PProductError";
import { PHalvesProductInstanceCreator, PSinglePartProductInstanceCreator, } from "./PProductInstanceCreatorState";
import { PProductUnavailabilityDetails } from "./PProductInstanceUnavailabilityDetails";
export class PProductInstanceCreator {
    constructor(params) {
        this.domain = params.domain;
        this.pProductInstanceCreatorPartFactory =
            params.pProductInstanceCreatorPartFactory;
        this.productInstanceAvailabilityService =
            params.productInstanceAvailabilityService;
        this.modifierInstanceAvailabilityService =
            params.modifierInstanceAvailabilityService;
        this.customParameterInstanceAvailabilityService =
            params.customParameterInstanceAvailabilityService;
        this.modifierInstancePriceService = params.modifierInstancePriceService;
        this.pModifierSectionCreatorFactory = params.pModifierSectionCreatorFactory;
        this.localizationProvider = params.localizationProvider;
        this.restaurant = params.restaurant;
        this.possibleProductTypes = params.possibleProductTypes;
        this._state = this.createState();
        makeAutoObservable(this);
    }
    get instance() {
        return this.domain.instance;
    }
    commonModifierOrder(productTypeIds) {
        const restaurantProductTypes = this.restaurant.productTypes;
        return productTypeIds
            .flatMap((productTypeId) => restaurantProductTypes.get(productTypeId).nonDivisibleModifiersOrder)
            .filter((modifierTypeId, index, array) => array.indexOf(modifierTypeId) === index);
    }
    createModifierInstanceCreator(domain, creatorScopeProvider) {
        return new PModifierInstanceCreator({
            domain,
            modifierInstanceAvailabilityService: this.modifierInstanceAvailabilityService,
            modifierInstancePriceService: this.modifierInstancePriceService,
            modifierType: this.restaurant.modifierTypes.get(domain.typeId),
            localizationProvider: this.localizationProvider,
            pModifierSectionCreatorFactory: this.pModifierSectionCreatorFactory,
            creatorScopeProvider,
        });
    }
    createCustomParameterInstanceCreator(domain) {
        return new PCustomParameterInstanceCreator({
            domain,
            customParameterInstanceAvailabilityService: this.customParameterInstanceAvailabilityService,
            customParameterType: this.restaurant.customParameterTypes.get(domain.typeId),
        });
    }
    createState() {
        const parts = this.domain.state;
        const productTypeIds = parts.productTypeIds.rawValues();
        const modifierOrder = this.commonModifierOrder(productTypeIds);
        const creatorScopeProvider = parts.creatorScopeProvider;
        const commonModifierInstanceCreators = new RArray(parts.commonModifierInstanceCreators.objects
            .map((m) => this.createModifierInstanceCreator(m, creatorScopeProvider))
            .sorted((a, b) => modifierOrder.findIndex((idx) => idx.eq(a.typeId)) -
            modifierOrder.findIndex((idx) => idx.eq(b.typeId))).raw).raw;
        const commonCustomParameterInstanceCreators = new RArray(parts.commonCustomParameterInstanceCreators.objects
            .map((c) => this.createCustomParameterInstanceCreator(c))
            .sorted((a, b) => a.name.localeCompare(b.name)).raw).raw;
        if (parts.isSplit) {
            const firstHalf = this.partCreator(parts.firstHalf, creatorScopeProvider);
            const secondHalf = this.partCreator(parts.secondHalf, creatorScopeProvider);
            return new PHalvesProductInstanceCreator({
                commonModifierInstanceCreators,
                commonCustomParameterInstanceCreators,
                firstHalf,
                secondHalf,
            });
        }
        else {
            const part = this.partCreator(parts.partCreator, creatorScopeProvider);
            return new PSinglePartProductInstanceCreator({
                commonModifierInstanceCreators,
                commonCustomParameterInstanceCreators,
                part,
            });
        }
    }
    get state() {
        return this._state;
    }
    partCreator(part, creatorScopeProvider) {
        return this.pProductInstanceCreatorPartFactory.create({
            domain: part,
            productTypeId: part.productType.id,
            possibleProductTypes: this.possibleProductTypes,
            creatorScopeProvider,
        });
    }
    get product() {
        return this.state.product;
    }
    updateState() {
        this._state = this.createState();
    }
    get error() {
        const availability = this.productInstanceAvailabilityService.availability(this.domain.instance);
        if (ProductInstanceCreatorState.isSplit(this.domain.state) &&
            availability.unavailabilityReasons.includes("HalvingAvailability")) {
            const { parametersDetails } = new PProductUnavailabilityDetails({
                availability,
                restaurant: this.restaurant,
            });
            if (parametersDetails.length > 0) {
                return PProductError.notDivisible(parametersDetails);
            }
        }
        return null;
    }
    validate() {
        this.state.validate();
    }
}
